import {
  EvaluationContext,
  FlagValue,
  JsonValue,
  Logger,
  OpenFeatureError,
  OpenFeatureErrorCode,
  Provider,
  ProviderMetadata,
  ResolutionDetails,
} from '@openfeature/web-sdk';
import { FeatureFlagsResponse, FeatureFlagValue } from '@/types/featureFlags';
import { callApi } from '@/hooks/useFetch';

export class BackendSyncProvider implements Provider {
  readonly metadata: ProviderMetadata = {
    name: 'BackendSyncProvider',
  };

  private _flags: Record<string, FeatureFlagValue> = {};
  private _lastSync = 0;
  private readonly _syncInterval = 5 * 60 * 1000; // 5 minutes
  private _logger?: Logger;

  constructor() {
    // The route ('/config/feature-flags') is resolved by callApi using the
    // standard `/api` prefix and the Vite dev-proxy / prod ingress — no
    // need to read VITE_API_URL directly here.
    this._loadFromCache();
  }

  async initialize(context?: EvaluationContext, logger?: Logger): Promise<void> {
    this._logger = logger;
    this._logger?.debug('BackendSyncProvider initializing...');
    
    try {
      await this._syncWithBackend();
      this._logger?.info('BackendSyncProvider initialized successfully');
    } catch (error) {
      this._logger?.error('BackendSyncProvider initialization failed:', error);
      // Don't throw - use cached/default values
    }
  }

  async onContextChange(oldContext: EvaluationContext, newContext: EvaluationContext): Promise<void> {
    this._logger?.debug('Context changed, syncing with backend...');
    try {
      await this._syncWithBackend(newContext);
    } catch (error) {
      this._logger?.error('Context change sync failed:', error);
    }
  }

  resolveBooleanEvaluation(flagKey: string, defaultValue: boolean, context?: EvaluationContext): ResolutionDetails<boolean> {
    const flagData = this._getFlag(flagKey);
    
    if (!flagData) {
      return {
        value: defaultValue,
        reason: 'DEFAULT',
      };
    }

    let value = defaultValue;
    if (typeof flagData.value === 'boolean') {
      value = flagData.value;
    } else if (typeof flagData.value === 'object' && flagData.value && 'enabled' in flagData.value) {
      value = Boolean(flagData.value.enabled);
    }

    return {
      value,
      variant: flagData.variant,
      reason: flagData.reason || 'STATIC',
      flagMetadata: flagData.metadata,
    };
  }

  resolveStringEvaluation(flagKey: string, defaultValue: string, context?: EvaluationContext): ResolutionDetails<string> {
    const flagData = this._getFlag(flagKey);
    
    if (!flagData) {
      return {
        value: defaultValue,
        reason: 'DEFAULT',
      };
    }

    const value = typeof flagData.value === 'string' ? flagData.value : defaultValue;

    return {
      value,
      variant: flagData.variant,
      reason: flagData.reason || 'STATIC',
      flagMetadata: flagData.metadata,
    };
  }

  resolveNumberEvaluation(flagKey: string, defaultValue: number, context?: EvaluationContext): ResolutionDetails<number> {
    const flagData = this._getFlag(flagKey);
    
    if (!flagData) {
      return {
        value: defaultValue,
        reason: 'DEFAULT',
      };
    }

    const value = typeof flagData.value === 'number' ? flagData.value : defaultValue;

    return {
      value,
      variant: flagData.variant,
      reason: flagData.reason || 'STATIC',
      flagMetadata: flagData.metadata,
    };
  }

  resolveObjectEvaluation<T extends JsonValue>(flagKey: string, defaultValue: T, context?: EvaluationContext): ResolutionDetails<T> {
    const flagData = this._getFlag(flagKey);
    
    if (!flagData) {
      return {
        value: defaultValue,
        reason: 'DEFAULT',
      };
    }

    const value = flagData.value !== undefined ? flagData.value as T : defaultValue;

    return {
      value,
      variant: flagData.variant,
      reason: flagData.reason || 'STATIC',
      flagMetadata: flagData.metadata,
    };
  }

  private _syncInFlight = false;

  private _getFlag(flagKey: string) {
    // Check if we need to sync. Single-flight + advancing _lastSync even on
    // failure: without both, a failing backend meant one fire-and-forget
    // /config/feature-flags request PER flag evaluation PER render — a silent
    // network storm. Failures now retry no sooner than the next interval.
    if (!this._syncInFlight && Date.now() - this._lastSync > this._syncInterval) {
      this._syncInFlight = true;
      this._lastSync = Date.now();
      this._syncWithBackend()
        .catch(error => {
          this._logger?.error('Background sync failed:', error);
        })
        .finally(() => {
          this._syncInFlight = false;
        });
    }

    return this._flags[flagKey];
  }

  private async _syncWithBackend(context?: EvaluationContext): Promise<void> {
    try {
      // callApi prefixes relative URLs with `/api` and goes through the
      // Vite dev proxy / prod ingress, so cookies + same-origin work.
      const data = await callApi<FeatureFlagsResponse>(
        '/config/feature-flags'
      );

      if (data.error) {
        throw new Error(data.error);
      }

      this._flags = data.flags;
      this._lastSync = Date.now();

      // Cache the flags
      this._saveToCache();

      this._logger?.debug('Successfully synced flags from backend', {
        flagCount: Object.keys(this._flags).length,
        provider: data.provider
      });

    } catch (error) {
      this._logger?.error('Failed to sync with backend:', error);

      // If we have no cached flags, use defaults
      if (Object.keys(this._flags).length === 0) {
        this._flags = this._getDefaultFlags();
        this._logger?.info('Using default flags due to sync failure');
      }

      throw error;
    }
  }

  private _loadFromCache(): void {
    try {
      const cached = localStorage.getItem('openfeature-backend-sync-flags');
      if (cached) {
        const data = JSON.parse(cached);
        this._flags = data.flags || {};
        this._lastSync = data.timestamp || 0;
        this._logger?.debug('Loaded flags from cache', { 
          flagCount: Object.keys(this._flags).length 
        });
      }
    } catch (error) {
      this._logger?.warn('Failed to load flags from cache:', error);
    }
  }

  private _saveToCache(): void {
    try {
      const data = {
        flags: this._flags,
        timestamp: this._lastSync,
      };
      localStorage.setItem('openfeature-backend-sync-flags', JSON.stringify(data));
    } catch (error) {
      this._logger?.warn('Failed to save flags to cache:', error);
    }
  }

  private _getDefaultFlags(): Record<string, FeatureFlagValue> {
    return {};
  }

  // Public method to force sync (useful for manual refresh)
  async forceSync(context?: EvaluationContext): Promise<void> {
    this._lastSync = 0; // Force sync
    await this._syncWithBackend(context);
  }
}